import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { add, deleteRecord, fetch, update } from '../services/PricesService';
import { Price } from '../types';

export const usePrice = (productId: string, priceId?: string) => {
    if (!productId) {
        throw new Error('productId is required parameter');
    }

    const queryClient = useQueryClient();

    // FETCH
    const priceQuery = useQuery({
        queryKey: ['price', priceId],
        queryFn: () => fetch(priceId!),
        enabled: !!priceId
    });

    // CREATE MUTATION
    const createMutation = useMutation({
        mutationFn: (values: Price) => {
            const { created, updated, ...rest } = values;
            return add({ ...rest, productId });
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['prices', productId] });
        }
    });

    // UPDATE MUTATION
    const updateMutation = useMutation({
        mutationFn: (values: Price) => {
            if (!priceId) throw new Error('priceId is required parameter');
            const { created, updated, ...rest } = values;
            return update(priceId, rest);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['prices', productId] });
            queryClient.invalidateQueries({ queryKey: ['price', priceId] });
        }
    });

    // DELETE MUTATION
    const deleteMutation = useMutation({
        mutationFn: () => {
            if (!priceId) throw new Error('priceId is required parameter');
            return deleteRecord(priceId);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['prices'] });
            queryClient.removeQueries({ queryKey: ['price', priceId] });
        }
    });

    return {
        record: priceQuery.data,
        loading: priceQuery.isLoading,
        fetchRecord: priceQuery.refetch,
        createRecord: createMutation.mutate,
        updateRecord: updateMutation.mutate,
        deleteRecord: deleteMutation.mutate,
        createMutation,
        updateMutation,
        deleteMutation
    };
};